import { useEffect, useRef } from "react";
import { css, keyframes } from "styled-components";

// 섹션 안의 `.reveal` 요소가 화면에 들어오면 `is-visible` 클래스를 붙여 등장 애니메이션을 시작한다.
// 한 번 나타난 요소는 다시 숨기지 않는다.
const useReveal = <T extends HTMLElement = HTMLElement>() => {
  const ref = useRef<T>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    if (typeof IntersectionObserver === "undefined") {
      root.querySelectorAll<HTMLElement>(".reveal").forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }
    );

    // 아직 나타나지 않은 요소만 관찰한다 (이미 관찰 중인 요소를 다시 observe 해도 무시된다)
    const observePending = () => {
      root.querySelectorAll<HTMLElement>(".reveal:not(.is-visible)").forEach((el) => observer.observe(el));
    };
    observePending();

    // 언어 전환처럼 key 가 바뀌어 카드가 새로 그려지면 is-visible 이 없는 채로 숨겨지므로, 새 요소도 관찰한다
    const mutationObserver = new MutationObserver(observePending);
    mutationObserver.observe(root, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  return ref;
};

// styled-components 에서 공통으로 쓰는 등장 애니메이션 스타일
// 카드의 hover 효과(transform, transition)와 겹치지 않도록 translate 속성 keyframe 으로 처리한다.
const revealIn = keyframes`
  from {
    opacity: 0;
    translate: 0 28px;
  }
  to {
    opacity: 1;
    translate: 0 0;
  }
`;

export const revealStyle = css`
  .reveal {
    opacity: 0;
  }
  .reveal.is-visible {
    animation: ${revealIn} 0.8s ease-out var(--reveal-delay, 0s) both;
  }
  @media (prefers-reduced-motion: reduce) {
    .reveal,
    .reveal.is-visible {
      opacity: 1;
      animation: none;
    }
  }
`;

export default useReveal;
